/* eslint-disable no-unused-expressions */
import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { authConfig } from '../configs/authConfig';
import { setLoader, setToast } from '../../store/reducers/create-proj-reducer';
import { env, isNullEmptyOrUndefined, notificationUrls } from './commonService.js';

const AUTH_REDIRECT_AT = 'authRedirectAt';
const AUTH_REDIRECT_COOLDOWN_MS = 60000;

const isNotificationApi = (url = '') => url.includes('orangeflower') || url.includes('notification');

const resolveAudience = (url = '') => (isNotificationApi(url)
  ? notificationUrls().baseUrl
  : env().audience);

function useCallApi() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const dispatch = useDispatch();

  const isAuthInteractionRequiredError = (err) => {
    const errorCode = String(err?.error || '').toLowerCase();
    const message = String(err?.message || '').toLowerCase();

    return ['consent_required', 'login_required', 'interaction_required'].includes(errorCode)
      || message.includes('consent required')
      || message.includes('login required')
      || message.includes('interaction required');
  };

  const redirectToLogin = (url) => {
    if (isNotificationApi(url)) return false;
    const lastRedirect = Number(sessionStorage.getItem(AUTH_REDIRECT_AT) || 0);
    if (Date.now() - lastRedirect < AUTH_REDIRECT_COOLDOWN_MS) return true;
    sessionStorage.setItem(AUTH_REDIRECT_AT, String(Date.now()));
    loginWithRedirect({
      audience: resolveAudience(url),
      scope: env().scope,
    });
    return true;
  };

  const fetchToken = useCallback(async (url) => {
    const token = await getAccessTokenSilently({
      audience: resolveAudience(url),
    });
    return token;
  }, [getAccessTokenSilently]);

  const connect = async (method, apiUrl, payload, okCallback, errCallback) => {
    if (!navigator.onLine) {
      dispatch(setLoader(false));
      !isMobile && dispatch(setToast({ openToast: true, toastLabel: 'application.internet.alert' }));
      return;
    }

    try {
      const token = await fetchToken(apiUrl);

      const response = await axios({
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        baseURL: authConfig.backendBaseUrl,
        url: apiUrl,
        data: payload,
        responseType: 'json',
      });

      if (response && response.status === 200 && response.data) {
        if (okCallback) okCallback(response.data);
      } else if (response && response.status === 401) {
        if (!redirectToLogin(apiUrl) && errCallback) errCallback(response);
      } else if (errCallback) {
        errCallback(response);
      }
    } catch (err) {
      console.error(`[connect] API call failed: ${method.toUpperCase()} ${apiUrl}`, err);
      if (err?.response?.status === 401 || isAuthInteractionRequiredError(err)) {
        if (!redirectToLogin(apiUrl) && errCallback) errCallback(err);
      } else if (errCallback) {
        errCallback(err);
      }
    }
  };

  const get = (apiUrl, okCallback, errCallback) => {
    connect('get', apiUrl, null, okCallback, errCallback);
  };

  const post = (apiUrl, payload, okCallback, errCallback) => {
    connect('post', apiUrl, payload, okCallback, errCallback);
  };

  const put = (apiUrl, payload, okCallback, errCallback) => {
    connect('put', apiUrl, payload, okCallback, errCallback);
  };

  const delet = (apiUrl, okCallback, errCallback) => {
    connect('delete', apiUrl, null, okCallback, errCallback);
  };

  const connectFileDownload = async (method, apiUrl, payload, okCallback, errCallback, token) => {
    try {
      const resolvedToken = !isNullEmptyOrUndefined(token) ? token : await fetchToken(apiUrl);

      const response = await axios({
        method,
        headers: {
          Authorization: `Bearer ${resolvedToken}`,
          'x-ms-version': '2020-10-02',
        },
        baseURL: authConfig.backendBaseUrl,
        url: apiUrl,
        data: payload,
        responseType: 'blob',
      });

      if (response && response.status === 200 && response.data) {
        if (okCallback) okCallback(response.data);
      } else if (errCallback) {
        errCallback(response);
      }
    } catch (err) {
      if (errCallback) {
        errCallback(err);
      }
    }
  };

  const fileDownload = (apiUrl, payload, okCallback, errCallback) => {
    connectFileDownload('post', apiUrl, payload, okCallback, errCallback, null);
  };

  const mediaRender = (apiUrl, okCallback, errCallback, token) => {
    connectFileDownload('get', apiUrl, null, okCallback, errCallback, token);
  };

  const connectCompressedFile = async (method, apiUrl, payload, okCallback, errCallback) => {
    try {
      const token = await fetchToken(apiUrl);

      const response = await axios({
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/zip',
        },
        baseURL: authConfig.backendBaseUrl,
        url: apiUrl,
        data: payload,
        responseType: 'json',
      });

      if (response && response.status === 200 && response.data) {
        if (okCallback) okCallback(response.data);
      } else if (errCallback) {
        errCallback(response);
      }
    } catch (err) {
      if (errCallback) {
        errCallback(err);
      }
    }
  };

  const postCompressedFile = (apiUrl, payload, okCallback, errCallback) => {
    connectCompressedFile('post', apiUrl, payload, okCallback, errCallback);
  };

  return {
    fetchToken, get, post, put, delet, fileDownload, mediaRender, postCompressedFile,
  };
}

export default useCallApi;
